/**
 * "Deadlines" — what keeps us legal to test, due within 30 days (#2096).
 *
 * Six gauges due 11/14 and Tristian's BPAT license (11/17) reached no one in
 * the office: expiry alerts are pinned off in production and only ever rang
 * the bell. This chip is their one home (alerts: one home each — never the
 * bell, never a push):
 *   · hidden when nothing is due; amber while everything is still good, red
 *     once anything is out of date;
 *   · click opens a flyout, soonest first, each row with one button to the
 *     page that fixes it.
 * The chip and the Command Center "Deadline radar" tile read ONE server list
 * (GET /api/command-center/deadlines). Office roles and the owner.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { orgTodayKey } from "@/lib/datetime";
import {
    DEADLINES_KEY,
    daysLeftOf,
    deadlinesChipState,
    dueWords,
    rowTone,
    shortDue,
    type DeadlinesView,
} from "@/lib/deadlines";

export function DeadlinesChip() {
    const { isOffice } = useAuth();
    const navigate = useNavigate();
    const q = useQuery({
        queryKey: DEADLINES_KEY,
        queryFn: () => api.get<DeadlinesView>("/api/command-center/deadlines"),
        enabled: !!isOffice,
        // Dates move once a day; a slow poll is plenty.
        refetchInterval: 15 * 60_000,
        staleTime: 5 * 60_000,
        retry: false,
    });
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    if (!isOffice) return null;
    const view = q.data;
    const state = deadlinesChipState(view);
    if (!state.show && !open) return null;
    const items = view?.items ?? [];
    const today = orgTodayKey();

    return (
        <>
            <button
                type="button"
                className={`dlchip t-${state.tone}`}
                data-testid="deadlines-chip"
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={`${state.label}${view?.outOfDate ? `, ${view.outOfDate} out of date` : ""}`}
                onClick={() => setOpen((o) => !o)}
            >
                <CalendarClock size={15} aria-hidden />
                <span>{state.label}</span>
            </button>
            <aside
                className={`bkfly${open ? " on" : ""}`}
                role="dialog"
                aria-label="Deadlines in the next 30 days"
                aria-hidden={!open}
                {...((open ? {} : { inert: "" }) as Record<string, string>)}
                data-testid="deadlines-flyout"
            >
                <div className="bkfly-h">
                    <b>Deadlines · next 30 days</b>
                    <button type="button" className="x" aria-label="Close" onClick={() => setOpen(false)}>
                        <X size={16} />
                    </button>
                </div>
                <div className="bkfly-b">
                    <div className="legend" aria-hidden>
                        <span>
                            <i style={{ background: "#B23B36" }} />
                            Out of date
                        </span>
                        <span>
                            <i style={{ background: "#C9772B" }} />
                            Due soon
                        </span>
                    </div>
                    {items.length === 0 ? (
                        <p className="empty">Nothing due in the next 30 days.</p>
                    ) : (
                        items.map((item, i) => {
                            const left = daysLeftOf(item, today);
                            return (
                                <article
                                    key={`${item.label}-${i}`}
                                    className={`bc ${rowTone(left)}`}
                                    data-testid="deadlines-card"
                                >
                                    <div className="bc-top">
                                        <div>
                                            <b>{item.label}</b>
                                            <span className="s">Last good day {shortDue(item.due_on, today)}</span>
                                        </div>
                                        <span className="when">{dueWords(left)}</span>
                                    </div>
                                    <div className="bc-acts">
                                        <button
                                            type="button"
                                            className="fb contact"
                                            onClick={() => {
                                                setOpen(false);
                                                navigate(item.href);
                                            }}
                                            data-testid="deadlines-open"
                                        >
                                            Open
                                        </button>
                                    </div>
                                </article>
                            );
                        })
                    )}
                </div>
            </aside>
        </>
    );
}

/** Scoped to the toolbar (.tbv3). The flyout reuses the booking flyout's look. */
export const deadlinesChipCss = `
  .tbv3 .dlchip{display:inline-flex;align-items:center;justify-content:center;gap:7px;height:34px;padding:0 13px;
    border-radius:10px;font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap;border:1px solid;
    font-variant-numeric:tabular-nums}
  .tbv3 .dlchip.t-amber{background:#C9772B;color:#fff;border-color:#C9772B}
  .tbv3 .dlchip.t-red{background:#B23B36;color:#fff;border-color:#B23B36}
  .tbv3 .dlchip:focus-visible{outline:3px solid #8fb4ee;outline-offset:2px}
  @media (max-width:720px){.tbv3 .dlchip{flex:1 1 auto}}
`;
